
import { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Video, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api/client';

interface RecordingStatus {
  status: 'enabled' | 'disabled' | 'recording' | 'processing' | 'error';
  lastRecording?: string;
  nextRecording?: string;
}

interface StreamRecordingControlsProps {
  streamId: number;
  onStatusChange?: (status: RecordingStatus) => void;
}

export function StreamRecordingControls({ streamId, onStatusChange }: StreamRecordingControlsProps) {
  const [status, setStatus] = useState<RecordingStatus>({ status: 'disabled' });
  const [isLoading, setIsLoading] = useState(false);
  const [isToggling, setIsToggling] = useState(false);

  const fetchStatus = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get<{ status: RecordingStatus }>(`api/streams/${streamId}/recording/status`);

      if (response.success && response.data) {
        const recordingStatus = response.data.status;
        setStatus(recordingStatus);
        onStatusChange?.(recordingStatus);
      } else {
        toast.error(response.error?.message || 'Failed to fetch recording status');
        console.error('Fetch recording status error:', response.error);
      }
    } catch (error) {
      toast.error('Failed to fetch recording status');
      console.error('Fetch recording status error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [streamId, onStatusChange]);

  const toggleRecording = async (enabled: boolean) => {
    setIsToggling(true);
    try {
      const response = await api.post(`api/streams/${streamId}/recording`, { enabled });

      if (response.success) {
        toast.success(enabled ? 'Recording enabled successfully' : 'Recording disabled successfully');
        fetchStatus();
      } else {
        toast.error(response.error?.message || (enabled ? 'Failed to enable recording' : 'Failed to disable recording'));
        console.error('Toggle recording error:', response.error);
      }
    } catch (error) {
      toast.error(enabled ? 'Failed to enable recording' : 'Failed to disable recording');
      console.error('Toggle recording error:', error);
    } finally {
      setIsToggling(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'enabled':
      case 'recording':
        return 'bg-green-500';
      case 'disabled':
        return 'bg-gray-400';
      case 'processing':
        return 'bg-yellow-500';
      case 'error':
        return 'bg-red-500';
      default:
        return 'bg-gray-400';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'enabled':
        return 'Recording Enabled';
      case 'disabled':
        return 'Recording Disabled';
      case 'recording':
        return 'Currently Recording';
      case 'processing':
        return 'Processing Recording';
      case 'error':
        return 'Recording Error';
      default:
        return 'Unknown Status';
    }
  };

  useEffect(() => {
    fetchStatus();
    
  }, [streamId]);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="relative">
              <Video className="h-6 w-6" />
              <div className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full ${getStatusColor(status.status)}`} />
            </div>
            <div className="min-w-0 flex-1">
              <CardTitle className="text-lg truncate">Recording Controls</CardTitle>
              <p className="text-sm text-muted-foreground truncate">Manage stream recording settings</p>
            </div>
          </div>
          <Button variant="outline" size="icon" onClick={fetchStatus} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Status Indicator */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${getStatusColor(status.status)}`} />
            <span className="font-medium">{getStatusText(status.status)}</span>
          </div>
          {status.status === 'error' && (
            <AlertCircle className="h-4 w-4 text-red-500" />
          )}
          {status.status === 'enabled' && (
            <CheckCircle className="h-4 w-4 text-green-500" />
          )}
        </div>

        {/* Toggle Switch */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="space-y-1">
            <Label htmlFor="recording-toggle" className="text-sm font-medium">
              Enable Stream Recording
            </Label>
            <p className="text-xs text-muted-foreground">
              When enabled, this stream will be automatically recorded
            </p>
          </div>
          <Switch
            id="recording-toggle"
            checked={status.status === 'enabled' || status.status === 'recording'}
            onCheckedChange={toggleRecording}
            disabled={isToggling || isLoading}
          />
        </div>

        {/* Status Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="space-y-2">
            <h4 className="font-medium">Current Status</h4>
            <p className="text-muted-foreground capitalize">{status.status}</p>
          </div>
          
          {status.lastRecording && (
            <div className="space-y-2">
              <h4 className="font-medium">Last Recording</h4>
              <p className="text-muted-foreground">
                {new Date(status.lastRecording).toLocaleString()}
              </p>
            </div>
          )}
          
          {status.nextRecording && (
            <div className="space-y-2">
              <h4 className="font-medium">Next Recording</h4>
              <p className="text-muted-foreground">
                {new Date(status.nextRecording).toLocaleString()}
              </p>
            </div>
          )}
        </div>
      </CardContent>
      
      <CardFooter className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div className="text-sm text-muted-foreground">
          Stream ID: {streamId}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="icon"
            onClick={fetchStatus}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            size="sm"
            disabled={isToggling || isLoading}
            onClick={() => toggleRecording(status.status !== 'enabled')}
          >
            {isToggling ? 'Updating...' : status.status === 'enabled' ? 'Disable Recording' : 'Enable Recording'}
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}